import { useMemo, useState } from "react"
import { HexColorPicker } from "react-colorful"
import Button, { type ButtonProps } from "../button/Button"
import Input, { type InputProps } from "./Input"
import { Label } from "./Label"
import { Popover, PopoverTrigger, PopoverContent } from "../Popover"
import { cn } from "../../../utils"

interface ColorPickerProps {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  variant?: "white" | "grey" | "transparent"
  tooltip?: string
}

const ColorPickerInput = ({
  disabled,
  value,
  onChange,
  onBlur,
  name,
  className,
  label,
  variant = "white",
  tooltip,
  ...props
}: Omit<ButtonProps, "value" | "onChange" | "onBlur" | "variant"> &
  ColorPickerProps &
  Pick<InputProps, "error" | "success" | "label">) => {
  const [open, setOpen] = useState(false)

  const parsedValue = useMemo(() => {
    return value ? value.toUpperCase() : "#FFFFFF"
  }, [value])

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <Label
        error={props.error}
        success={props.success}
        className="mb-2"
        disabled={disabled}
        tooltip={tooltip}
      >
        {label}
      </Label>
      <div className="flex items-center">
        <PopoverTrigger asChild disabled={disabled} onBlur={onBlur}>
          <Button
            {...props}
            type="button"
            variant="unstyled"
            className={cn(
              "flex bg-white rounded-e-none border-r-0 focus-visible:ring-[1px] focus-visible:ring-primary-dark-blue focus-visible:border-transparent data-[state=open]:border-transparent data-[state=open]:ring-[1px] data-[state=open]:ring-primary-dark-blue z-10 aspect-square size-9",
              className,
            )}
            disabled={disabled}
            style={{
              backgroundColor: parsedValue,
            }}
          >
            <div />
          </Button>
        </PopoverTrigger>

        <Input
          maxLength={7}
          onChange={(e) => {
            onChange(e?.currentTarget?.value)
          }}
          value={parsedValue}
          className="rounded-e-sm rounded-s-none"
          containerClassName="flex-1"
          name={name}
          disabled={disabled}
          error={props.error}
          success={props.success}
          variant={variant}
        />
      </div>
      <PopoverContent className="w-full">
        <HexColorPicker color={parsedValue} onChange={onChange} />
      </PopoverContent>
    </Popover>
  )
}
ColorPickerInput.displayName = "ColorPicker"

export { ColorPickerInput }
